import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, BadgeCheck } from "lucide-react";
import { apiUrl } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { gaTrack } from "@/lib/analytics";

/* The board, running across the width of the hero.

   This replaces the single rotating card that sat in a right-hand column. One
   card at a time answers "is anybody here" with a yes; a row of them answers it
   with a marketplace, and the headline now says "browse available talent", so
   the hero has to look like something you could browse.

   It scrolls rather than swaps. A rail that moves is read as a feed with more
   in it than fits, which is true — there are far more people on the board than
   the twenty this shows. A carousel that swaps in place is read as a slideshow
   with a fixed number of slides.

   Same endpoint and the same one-row-per-person rule as HeroAvailability and
   AvailabilityTicker, so no two surfaces can disagree about who is on the
   board. Renders NOTHING when the fetch fails or the board is empty and says so
   via onEmpty: a rail captioned "available now" with nothing in it answers the
   visitor's question with a no. */

/* Ask for a full page and keep the ones with a photograph. 39 of the 114 people
   on the board have one, so a page of 50 yields roughly 18 — enough for a rail
   that never visibly repeats.

   A card is mostly a face. A row of monogram circles is a row of initials, which
   argues that nobody has finished a profile rather than that real people are
   here. MIN_WITH_PHOTOS is the safety net: if the board ever shifts so that
   almost nobody has a picture, showing monograms beats showing nothing. */
/* Only show a score that distinguishes somebody.
   The board today is 76 people at 43, 39 at 55, one at 73: the number is almost
   entirely "has a photo" (12 points) and "has a headline" (8), because one
   person of 118 has uploaded a credential and nobody has a rating from a
   completed contract yet. Printing 55 on every card would not argue that these
   people are known -- it would argue that the score means nothing, on the page
   that claims it does. Above 60 the number had to be earned by a credential or
   a rating, so it is worth showing. Lower this as the board fills in. */
const SCORE_FLOOR = 60;

/* The pool has to be much larger than the window, or shuffling changes very
   little: the endpoint returns most-recent-first, so a pool of 50 was a
   shuffle of last week's signups and everybody further down the board could
   never appear. 250 covers every person currently holding a live availability
   posting (232 on 2026-09-15), so the rail draws from the whole board and the
   twenty shown are a real sample of it rather than the front of a queue.

   One request, one row per person, and it is asynchronous -- nothing on the
   page waits for it. */
const POOL = 250;
const SHOW = 20;
const MIN_WITH_PHOTOS = 6;

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* First clause, capped, with dangling connectives trimmed — the same shaping
   HeroAvailability does, so a headline reads the same in both places. */
function shortOffer(title) {
  const t = (title || "").trim();
  if (!t) return "";
  const clause = t.split(",")[0].trim();
  const use = clause.split(/\s+/).length >= 3 ? clause : t;
  const words = use.split(/\s+/).slice(0, 8);
  while (words.length && /^(and|or|for|with|the|a|an|to|of|in)$/i.test(words[words.length - 1])) {
    words.pop();
  }
  return words.join(" ");
}

function Avatar({ src, name }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img className="htr__av" src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
  }
  return <span className="htr__av htr__av--mono" aria-hidden="true">{initials(name)}</span>;
}

export default function HeroTalentRail({ onEmpty }) {
  const [people, setPeople] = useState([]);
  const emptyCb = useRef(onEmpty);
  emptyCb.current = onEmpty;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // per_poster: one row per person. Without it these are postings, and on
        // the live board 50 postings belong to about 20 people.
        const res = await fetch(
          apiUrl(`/api/accounts/public/postings/?poster_type=contractor&per_poster=1&limit=${POOL}`),
          { credentials: "omit", mode: "cors" }
        );
        const data = res.ok ? await res.json().catch(() => ({})) : {};
        const rows = Array.isArray(data?.results) ? data.results : [];
        if (!alive) return;
        const seen = new Set();
        const out = [];
        for (const r of rows) {
          const name = (r.poster_name || "").trim();
          const offer = shortOffer(r.title);
          if (!name || !offer || seen.has(name)) continue;
          seen.add(name);
          out.push({
            id: r.id,
            // The public, logged-out profile lives at /c/:slug. Without one
            // there is nowhere specific to send them, so the card falls back to
            // the catalog rather than linking to a 404.
            slug: (r.poster_slug || "").trim(),
            name,
            offer,
            location: r.location || "",
            photo: r.poster_image_url || "",
            // Absent until the API carrying poster_sift_score is deployed, and
            // absent again for anyone it cannot be computed for. A missing
            // score shows nothing rather than a zero somebody would read as a
            // judgement.
            score: Number.isFinite(r.poster_sift_score) ? r.poster_sift_score : null,
            // Only actively-verified licenses reach the payload, so presence is
            // the whole test.
            verified: Array.isArray(r.poster_verified_credentials) && r.poster_verified_credentials.length > 0,
          });
        }
        /* Shuffled, not the newest fifty.
           The endpoint returns most-recent-first, so the rail was a window onto
           whoever signed up last week and the same faces sat there until new
           people pushed them off. That is unfair to everybody further down the
           board -- being on the hero is the most valuable placement we have,
           and it was going to whoever happened to be recent -- and it makes the
           page look static to anybody who visits twice.

           Fisher-Yates over the pool, then take SHOW. Shuffling BEFORE the
           photo filter matters: filtering first and shuffling after would keep
           drawing from the same newest-with-a-photo set. */
        const shuffled = out.slice();
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const withPhoto = shuffled.filter((p) => p.photo);
        const use = (withPhoto.length >= MIN_WITH_PHOTOS ? withPhoto : shuffled).slice(0, SHOW);
        setPeople(use);
        if (!use.length) emptyCb.current?.();
      } catch {
        if (alive) { setPeople([]); emptyCb.current?.(); }
      }
    })();
    return () => { alive = false; };
  }, []);

  if (people.length < 3) return null;

  /* Duplicated once so the track can loop without a seam: the animation
     translates exactly -50%, which lands the copy where the original started.
     The duplicate is inert to assistive tech and to the tab order. */
  const track = [...people, ...people];

  return (
    <div className="htr">
      <style>{CSS}</style>
      <div className="htr__mask">
        <div className="htr__track" style={{ "--htr-n": people.length }}>
          {track.map((p, i) => {
            const dupe = i >= people.length;
            return (
              <Link
                key={`${p.id}-${i}`}
                to={p.slug ? `/c/${p.slug}` : "/browse?show=person"}
                className="htr__card"
                tabIndex={dupe ? -1 : undefined}
                aria-hidden={dupe ? "true" : undefined}
                aria-label={`${p.name} - ${p.offer}${p.location ? `, ${p.location}` : ""}`}
                onClick={() => {
                  trackEvent("hero_rail_click", {
                    account_type: "organization", slug: p.slug || null, to_profile: !!p.slug,
                  });
                  gaTrack("hero_rail_click", { account_type: "organization", to_profile: !!p.slug });
                }}
              >
                <span className="htr__glow" aria-hidden="true" />
                <span className="htr__head">
                  <span className="htr__avwrap">
                    <Avatar src={p.photo} name={p.name} />
                    {p.verified && (
                      <span className="htr__badge" aria-label="Verified credentials">
                        <BadgeCheck size={13} strokeWidth={2.6} />
                      </span>
                    )}
                  </span>
                  <span className="htr__meta">
                    <span className="htr__live">
                      <span className="htr__dot" aria-hidden="true" />
                      Available
                    </span>
                    {p.score != null && p.score >= SCORE_FLOOR && (
                      <span className="htr__score" title="Sift Score: verified credentials, completed contracts and ratings">
                        <b>{p.score}</b> Sift Score
                      </span>
                    )}
                  </span>
                </span>
                <span className="htr__name">{p.name}</span>
                <span className="htr__offer">{p.offer}</span>
                {p.location && (
                  <span className="htr__loc">
                    <MapPin size={12} strokeWidth={2} aria-hidden="true" /> {p.location}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const CSS = `
/* The carousel measures the same as everything else on the page. It used to run
   full-bleed off both edges, which made it the one element that had escaped its
   column, the page looked like two different layouts stacked. The mask at each
   end does the work the bleed was doing: it says the row continues without
   letting it break the grid. */
.htr { width:100%; }
.htr__mask { overflow:hidden; padding:6px 0 18px;
  -webkit-mask-image:linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
  mask-image:linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent); }

/* 8s per card, scaled by how many there are, so a board of twenty does not fly
   past faster than a board of five. */
.htr__track { display:flex; gap:14px; width:max-content;
  animation:htrScroll calc(var(--htr-n) * 8s) linear infinite; }
.htr__mask:hover .htr__track, .htr__track:focus-within { animation-play-state:paused; }
@keyframes htrScroll { to { transform:translateX(-50%); } }

/* Translucent grey glass.

   Two earlier passes failed in opposite directions. Near-white cards on a
   #FAF8F2 ground had almost no contrast and read as faint rectangles; solid
   dark ones had plenty of contrast but stopped being glass, they were panels
   sitting on the page rather than part of it.

   This is a warm grey tint at low opacity with a heavy blur, so the cream
   ground and the hero wash both read through the card and it belongs to what is
   behind it. The white hairline along the top edge is what makes it glass
   rather than a translucent rectangle: it is the lit edge of a pane.

   Solid first as the fallback, without backdrop-filter a 30%-opacity card
   would be a smear, so those browsers get a plain surface instead. */
.htr__card { position:relative; overflow:hidden; flex:0 0 auto; width:272px;
  display:flex; flex-direction:column; gap:2px; padding:16px 17px 15px;
  border-radius:var(--r-lg); text-decoration:none;
  background:var(--surface); border:1px solid var(--border);
  box-shadow:var(--shadow-sm);
  transition:transform .25s cubic-bezier(.22,.61,.36,1), box-shadow .25s, border-color .25s; }
@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .htr__card {
    background:linear-gradient(158deg, rgba(255,255,255,.46) 0%, rgba(203,197,184,.24) 100%);
    backdrop-filter:saturate(150%) blur(22px);
    -webkit-backdrop-filter:saturate(150%) blur(22px);
    border-color:rgba(27,24,19,.17);
    box-shadow:0 1px 0 rgba(255,255,255,.85) inset,
               0 18px 40px -26px rgba(27,24,19,.42); }
}
.htr__card:hover { transform:translateY(-4px); border-color:var(--brand);
  box-shadow:0 1px 0 rgba(255,255,255,.9) inset,
             0 0 0 1px rgba(21,96,74,.18),
             0 26px 52px -26px rgba(27,24,19,.5); }
/* Keyboard users get the same affordance as a pointer, and the ring has to be
   visible on glass, so it is drawn rather than left to the UA default. */
.htr__card:focus-visible { outline:none; border-color:var(--brand);
  box-shadow:0 0 0 3px var(--brand-tint), 0 0 0 4px var(--brand); }

/* A green wash rising from the bottom left, so the card has a centre of gravity
   and picks up the brand without needing a coloured border. */
.htr__glow { position:absolute; inset:auto -30% -60% -30%; height:170px; pointer-events:none;
  background:radial-gradient(60% 100% at 28% 100%, rgba(21,96,74,.10), transparent 72%); }

.htr__head { display:flex; align-items:flex-start; justify-content:space-between;
  gap:10px; margin-bottom:11px; position:relative; }

.htr__avwrap { position:relative; flex:0 0 auto; }
.htr__av { width:50px; height:50px; border-radius:999px; object-fit:cover; display:block;
  box-shadow:0 0 0 2px rgba(255,255,255,.75), 0 0 0 3px rgba(27,24,19,.06); }
.htr__av--mono { display:grid; place-items:center; background:var(--brand-tint);
  color:var(--brand-ink); font-weight:700; font-size:1rem; }
.htr__badge { position:absolute; right:-3px; bottom:-3px; width:19px; height:19px;
  border-radius:999px; background:var(--brand); color:#fff; display:grid; place-items:center;
  border:2px solid rgba(255,255,255,.9); }

/* The live dot is the only motion inside a card, and it is the one thing on it
   that is time-sensitive: this person is open for work right now. */
.htr__meta { display:flex; flex-direction:column; align-items:flex-end; gap:5px; }

/* The score reads as a figure, not a badge: the number carries the weight and
   the label is small beside it, so a row of cards can be compared at a glance
   rather than each one shouting the same word. */
.htr__score { display:inline-flex; align-items:baseline; gap:4px; white-space:nowrap;
  font-family:var(--font-mono); font-size:0.59375rem; letter-spacing:.08em;
  text-transform:uppercase; color:var(--text-tertiary); }
.htr__score b { font-size:0.8125rem; font-weight:700; letter-spacing:-.01em;
  color:var(--brand); font-variant-numeric:tabular-nums; }

.htr__live { display:inline-flex; align-items:center; gap:5px; font-family:var(--font-mono);
  font-size:0.59375rem; letter-spacing:.12em; text-transform:uppercase; color:var(--brand-ink);
  background:rgba(234,241,237,.72); border:1px solid rgba(21,96,74,.18);
  border-radius:var(--r-pill); padding:4px 9px 4px 7px; white-space:nowrap; }
.htr__dot { width:5px; height:5px; border-radius:999px; background:var(--brand);
  box-shadow:0 0 7px rgba(21,96,74,.55);
  animation:htrPulse 2.4s ease-in-out infinite; }
@keyframes htrPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.4; transform:scale(.75); } }

.htr__name { position:relative; font-size:0.9375rem; font-weight:600; color:var(--ink);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.htr__offer { position:relative; font-size:0.8125rem; line-height:1.35; color:var(--text-secondary);
  margin-top:2px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;
  overflow:hidden; min-height:35px; }
.htr__loc { position:relative; display:flex; align-items:center; gap:4px; font-size:0.75rem;
  color:var(--text-tertiary); margin-top:9px; }

@media (prefers-reduced-motion: reduce) {
  .htr__mask { overflow-x:auto; }
  .htr__track { animation:none; }
  .htr__dot { animation:none; }
  .htr__card { transition:none; }
}
`;
